import { useState, useMemo } from 'react';
import type { ComponentType } from 'react';
import {
  // Navigation
  ArrowLeftIcon,
  ArrowRightIcon,
  ArrowUpIcon,
  ArrowDownIcon,
  ChevronLeftIcon,
  ChevronRightIcon,
  ChevronDownIcon,
  ChevronUpIcon,
  ExternalLinkIcon,
  HomeIcon,
  MenuIcon,
  // Actions
  PlusIcon,
  MinusIcon,
  PencilIcon,
  TrashIcon,
  SaveIcon,
  DownloadIcon,
  UploadIcon,
  ShareIcon,
  SendIcon,
  UndoIcon,
  RedoIcon,
  MoreHorizontalIcon,
  MoreVerticalIcon,
  // Status
  CheckIcon,
  XIcon,
  AlertTriangleIcon,
  AlertCircleIcon,
  InfoIcon,
  HelpCircleIcon,
  CheckCircleIcon,
  XCircleIcon,
  BanIcon,
  // Data
  EyeIcon,
  EyeOffIcon,
  CalendarIcon,
  ClockIcon,
  FileIcon,
  FolderIcon,
  ImageIcon,
  LinkIcon,
  // User / Auth
  UserIcon,
  UsersIcon,
  UserPlusIcon,
  LogOutIcon,
  LogInIcon,
  KeyIcon,
  LockIcon,
  ShieldIcon,
  // Communication
  MailIcon,
  PhoneIcon,
  MessageSquareIcon,
  BellIcon,
  // Layout
  LayoutGridIcon,
  ListIcon,
  MaximizeIcon,
  MinimizeIcon,
  // Media
  PlayIcon,
  PauseIcon,
  CameraIcon,
  FilmIcon,
  MusicIcon,
  // Commerce
  CreditCardIcon,
  TagIcon,
  ReceiptIcon,
  // Custom icons
  SunIcon,
  MoonIcon,
  GlobeIcon,
  SettingsIcon,
  CopyIcon,
  RefreshIcon,
  // Mailbox icons
  AddressBookIcon,
  AttachmentIcon,
  DraftIcon,
  FilterIcon,
  FlagIcon,
  ForwardIcon,
  InboxIcon,
  ReplyAllIcon,
  ReplyIcon,
  SearchIcon,
  SelectIcon,
  SpamIcon,
} from '@repo/ui-web';
import type { IconProps } from '@repo/ui-web';
import { useCssTokens } from '../../hooks/useCssTokens';

const iconTokens = [
  { token: 'icon-size-sm', label: 'sm' },
  { token: 'icon-size-md', label: 'md' },
  { token: 'icon-size-lg', label: 'lg' },
  { token: 'icon-size-xl', label: 'xl' },
];

const sizeShowcaseIcons = [HomeIcon, SettingsIcon, UserIcon, BellIcon];

interface IconEntry {
  name: string;
  icon: ComponentType<IconProps | { className?: string }>;
}

interface IconCategory {
  name: string;
  icons: IconEntry[];
}

const iconCategories: IconCategory[] = [
  {
    name: 'Navigation',
    icons: [
      { name: 'Home', icon: HomeIcon },
      { name: 'ArrowLeft', icon: ArrowLeftIcon },
      { name: 'ArrowRight', icon: ArrowRightIcon },
      { name: 'ArrowUp', icon: ArrowUpIcon },
      { name: 'ArrowDown', icon: ArrowDownIcon },
      { name: 'ChevronLeft', icon: ChevronLeftIcon },
      { name: 'ChevronRight', icon: ChevronRightIcon },
      { name: 'ChevronDown', icon: ChevronDownIcon },
      { name: 'ChevronUp', icon: ChevronUpIcon },
      { name: 'ExternalLink', icon: ExternalLinkIcon },
      { name: 'Menu', icon: MenuIcon },
    ],
  },
  {
    name: 'Actions',
    icons: [
      { name: 'Plus', icon: PlusIcon },
      { name: 'Minus', icon: MinusIcon },
      { name: 'Pencil', icon: PencilIcon },
      { name: 'Copy', icon: CopyIcon },
      { name: 'Trash', icon: TrashIcon },
      { name: 'Save', icon: SaveIcon },
      { name: 'Download', icon: DownloadIcon },
      { name: 'Upload', icon: UploadIcon },
      { name: 'Share', icon: ShareIcon },
      { name: 'Send', icon: SendIcon },
      { name: 'Undo', icon: UndoIcon },
      { name: 'Redo', icon: RedoIcon },
      { name: 'MoreHorizontal', icon: MoreHorizontalIcon },
      { name: 'MoreVertical', icon: MoreVerticalIcon },
      { name: 'Refresh', icon: RefreshIcon },
      { name: 'Filter', icon: FilterIcon },
      { name: 'Select', icon: SelectIcon },
    ],
  },
  {
    name: 'Status',
    icons: [
      { name: 'Check', icon: CheckIcon },
      { name: 'X', icon: XIcon },
      { name: 'AlertTriangle', icon: AlertTriangleIcon },
      { name: 'AlertCircle', icon: AlertCircleIcon },
      { name: 'Info', icon: InfoIcon },
      { name: 'HelpCircle', icon: HelpCircleIcon },
      { name: 'CheckCircle', icon: CheckCircleIcon },
      { name: 'XCircle', icon: XCircleIcon },
      { name: 'Ban', icon: BanIcon },
    ],
  },
  {
    name: 'Data',
    icons: [
      { name: 'Search', icon: SearchIcon },
      { name: 'Eye', icon: EyeIcon },
      { name: 'EyeOff', icon: EyeOffIcon },
      { name: 'Calendar', icon: CalendarIcon },
      { name: 'Clock', icon: ClockIcon },
      { name: 'File', icon: FileIcon },
      { name: 'Folder', icon: FolderIcon },
      { name: 'Image', icon: ImageIcon },
      { name: 'Link', icon: LinkIcon },
    ],
  },
  {
    name: 'User / Auth',
    icons: [
      { name: 'User', icon: UserIcon },
      { name: 'Users', icon: UsersIcon },
      { name: 'UserPlus', icon: UserPlusIcon },
      { name: 'LogOut', icon: LogOutIcon },
      { name: 'LogIn', icon: LogInIcon },
      { name: 'Key', icon: KeyIcon },
      { name: 'Lock', icon: LockIcon },
      { name: 'Shield', icon: ShieldIcon },
    ],
  },
  {
    name: 'Communication',
    icons: [
      { name: 'Mail', icon: MailIcon },
      { name: 'Phone', icon: PhoneIcon },
      { name: 'MessageSquare', icon: MessageSquareIcon },
      { name: 'Bell', icon: BellIcon },
    ],
  },
  {
    name: 'Layout',
    icons: [
      { name: 'LayoutGrid', icon: LayoutGridIcon },
      { name: 'List', icon: ListIcon },
      { name: 'Maximize', icon: MaximizeIcon },
      { name: 'Minimize', icon: MinimizeIcon },
    ],
  },
  {
    name: 'Media',
    icons: [
      { name: 'Play', icon: PlayIcon },
      { name: 'Pause', icon: PauseIcon },
      { name: 'Camera', icon: CameraIcon },
      { name: 'Film', icon: FilmIcon },
      { name: 'Music', icon: MusicIcon },
    ],
  },
  {
    name: 'Commerce',
    icons: [
      { name: 'CreditCard', icon: CreditCardIcon },
      { name: 'Tag', icon: TagIcon },
      { name: 'Receipt', icon: ReceiptIcon },
    ],
  },
  {
    name: 'Theme',
    icons: [
      { name: 'Sun', icon: SunIcon },
      { name: 'Moon', icon: MoonIcon },
      { name: 'Globe', icon: GlobeIcon },
      { name: 'Settings', icon: SettingsIcon },
    ],
  },
  {
    name: 'Mailbox',
    icons: [
      { name: 'Inbox', icon: InboxIcon },
      { name: 'Draft', icon: DraftIcon },
      { name: 'Flag', icon: FlagIcon },
      { name: 'Reply', icon: ReplyIcon },
      { name: 'ReplyAll', icon: ReplyAllIcon },
      { name: 'Forward', icon: ForwardIcon },
      { name: 'Attachment', icon: AttachmentIcon },
      { name: 'Spam', icon: SpamIcon },
      { name: 'AddressBook', icon: AddressBookIcon },
    ],
  },
];

export function IconographyShowcase() {
  const values = useCssTokens(iconTokens.map((item) => item.token));
  const [filter, setFilter] = useState('');

  const filteredCategories = useMemo(() => {
    const query = filter.toLowerCase().trim();
    if (!query) return iconCategories;

    return iconCategories
      .map((category) => ({
        ...category,
        icons: category.icons.filter(
          (entry) =>
            entry.name.toLowerCase().includes(query) || category.name.toLowerCase().includes(query),
        ),
      }))
      .filter((category) => category.icons.length > 0);
  }, [filter]);

  return (
    <div className="space-y-8">
      {/* Icon sizes */}
      <div className="space-y-3">
        <h4 className="text-sm font-semibold text-foreground">Icon sizes</h4>
        <div className="grid grid-cols-4 gap-3">
          {iconTokens.map((item) => (
            <div key={item.token} className="rounded-2xl border border-border/40 p-3">
              <p className="text-xs uppercase tracking-[0.2em] text-muted-foreground">
                {item.label}
              </p>
              <div className="mt-2 flex items-center gap-2">
                {sizeShowcaseIcons.map((Icon, index) => (
                  <Icon
                    key={index}
                    className="text-foreground"
                    style={{
                      width: values[item.token] || '20px',
                      height: values[item.token] || '20px',
                    }}
                  />
                ))}
              </div>
              <p className="mt-1 text-xs text-muted-foreground">{values[item.token]}</p>
            </div>
          ))}
        </div>
      </div>

      {/* Stroke widths */}
      <div className="space-y-3">
        <h4 className="text-sm font-semibold text-foreground">Stroke widths</h4>
        <div className="grid grid-cols-2 gap-3">
          {[
            { label: 'Regular', value: 1.5 },
            { label: 'Bold', value: 2 },
          ].map((variant) => (
            <div key={variant.label} className="rounded-2xl border border-border/40 p-3">
              <p className="text-xs uppercase tracking-[0.2em] text-muted-foreground">
                {variant.label}
              </p>
              <div className="mt-2 flex items-center gap-3">
                {sizeShowcaseIcons.map((Icon, index) => (
                  <Icon
                    key={index}
                    className="size-icon-lg text-foreground"
                    strokeWidth={variant.value}
                  />
                ))}
              </div>
              <p className="mt-1 text-xs text-muted-foreground">
                stroke-width: {variant.value}
              </p>
            </div>
          ))}
        </div>
      </div>

      {/* Icon gallery */}
      <div className="space-y-3">
        <h4 className="text-sm font-semibold text-foreground">Icon gallery</h4>
        <div className="relative">
          <SearchIcon
            className="pointer-events-none absolute left-3 top-1/2 -translate-y-1/2 text-muted-foreground"
            style={{ width: 16, height: 16 }}
          />
          <input
            type="text"
            value={filter}
            onChange={(e) => setFilter(e.target.value)}
            placeholder="Filter icons..."
            className="w-full rounded-xl border border-border/40 bg-transparent py-2 pl-9 pr-3 text-sm text-foreground placeholder:text-muted-foreground focus:border-input focus:outline-none focus:ring-2 focus:ring-ring/25"
          />
        </div>

        {filteredCategories.length === 0 && (
          <p className="py-8 text-center text-sm text-muted-foreground">
            No icons match &quot;{filter}&quot;
          </p>
        )}

        {filteredCategories.map((category) => (
          <div key={category.name} className="space-y-2">
            <p className="text-xs font-medium uppercase tracking-[0.15em] text-muted-foreground">
              {category.name}
            </p>
            <div className="grid grid-cols-4 gap-2 sm:grid-cols-6 md:grid-cols-8 lg:grid-cols-10">
              {category.icons.map((entry) => {
                const Icon = entry.icon;
                return (
                  <div
                    key={entry.name}
                    className="flex flex-col items-center gap-1.5 rounded-xl border border-border/40 p-2.5 transition-colors hover:bg-muted/50"
                  >
                    <Icon className="size-icon-md text-foreground" />
                    <p className="max-w-full truncate text-[10px] text-muted-foreground">{entry.name}</p>
                  </div>
                );
              })}
            </div>
          </div>
        ))}
      </div>

      {/* Logo usage */}
      <div className="space-y-3">
        <h4 className="text-sm font-semibold text-foreground">Logo usage</h4>
        <div className="grid gap-3 sm:grid-cols-2">
          <div className="rounded-2xl border border-border/40 bg-card/80 p-4">
            <p className="text-xs uppercase tracking-[0.2em] text-muted-foreground">Full logo</p>
            <img className="mt-3 h-8 w-auto" src="/brand/logo-full.png" alt="Brand logo full" />
            <p className="mt-2 text-xs text-muted-foreground">Use on light backgrounds.</p>
          </div>
          <div className="rounded-2xl border border-border/40 bg-card/80 p-4">
            <p className="text-xs uppercase tracking-[0.2em] text-muted-foreground">Mark</p>
            <img className="mt-3 h-8 w-auto" src="/brand/logo-400.png" alt="Brand logo mark" />
            <p className="mt-2 text-xs text-muted-foreground">Minimum size 24px height.</p>
          </div>
        </div>
      </div>
    </div>
  );
}
